// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Analytic collision primitives.
//
// There is no triangle soup anywhere in the collision world. A triangulated arc stays
// faceted no matter how many triangles you spend on it, and a faceted arc makes an orbit
// shot rattle in a way players recognise instantly as fake. See 02 §6.
//
// Every query answers the same three questions: is the ball touching, which way is out,
// and how far in. Contacts are written into a caller-supplied object so the hot path
// allocates nothing.

import type { Material, MaterialName } from './materials'
import { PENETRATION_SLOP } from './constants'
import { TWO_PI, cos, sin } from '../math/deterministic'

// Numeric discriminants rather than string kinds: this is the innermost loop in the
// product, and `verbatimModuleSyntax` rules out `const enum`.
export const KIND_SEGMENT = 0
export const KIND_ARC = 1
export const KIND_CIRCLE = 2

export interface SegmentCollider {
  readonly kind: typeof KIND_SEGMENT
  readonly ax: number
  readonly ay: number
  readonly bx: number
  readonly by: number
  /** Unit normal pointing into the play area. Required when `oneSided`. */
  readonly nx: number
  readonly ny: number
  readonly oneSided: boolean
  readonly material: MaterialName
  /** Optional device this collider belongs to, for event dispatch. */
  readonly device: number
  /**
   * Height layer this collider exists on. 0 is the playfield.
   *
   * A ball only ever touches geometry on its own layer, which is what stops a ramp's walls
   * from being an invisible obstacle to a ball rolling underneath them.
   */
  readonly layer: number
  readonly padding?:number
  /**
   * Scatter angle for this surface, radians, or −1 to use the material's.
   *
   * A machine may declare a surface as outlane geometry (04 §5 `outlane`), which swaps the
   * material's scatter for `physics.outlaneScatterDegrees`. PH-046 is explicit about why the
   * outlane in particular gets its own number: a perfectly deterministic outlane is either
   * always fatal or never fatal, and both are wrong.
   */
  readonly scatter: number
}

export interface ArcCollider {
  readonly kind: typeof KIND_ARC
  readonly cx: number
  readonly cy: number
  readonly radius: number
  /**
   * `false` — the ball is *contained within* the circle. A concave rounded corner, which
   * is what the top shoulders of a playfield are.
   * `true` — the ball stays *outside*. A convex obstacle.
   *
   * Getting this backwards is the easiest way to build an unplayable machine, which is
   * why the Builder cross-checks it against a flood-fill reachability pass.
   */
  readonly insideOut: boolean
  /** Unit vector bisecting the arc, and the cosine of its half-span. */
  readonly midX: number
  readonly midY: number
  readonly cosHalfSpan: number
  readonly material: MaterialName
  readonly device: number
  readonly layer: number
  readonly padding?:number
  /**
   * Scatter angle for this surface, radians, or −1 to use the material's.
   *
   * A machine may declare a surface as outlane geometry (04 §5 `outlane`), which swaps the
   * material's scatter for `physics.outlaneScatterDegrees`. PH-046 is explicit about why the
   * outlane in particular gets its own number: a perfectly deterministic outlane is either
   * always fatal or never fatal, and both are wrong.
   */
  readonly scatter: number
}

export interface CircleCollider {
  readonly kind: typeof KIND_CIRCLE
  readonly cx: number
  readonly cy: number
  readonly radius: number
  readonly material: MaterialName
  readonly device: number
  readonly layer: number
  readonly padding?:number
  /**
   * Scatter angle for this surface, radians, or −1 to use the material's.
   *
   * A machine may declare a surface as outlane geometry (04 §5 `outlane`), which swaps the
   * material's scatter for `physics.outlaneScatterDegrees`. PH-046 is explicit about why the
   * outlane in particular gets its own number: a perfectly deterministic outlane is either
   * always fatal or never fatal, and both are wrong.
   */
  readonly scatter: number
}

export type Collider = SegmentCollider | ArcCollider | CircleCollider

/** A resolved touch. Reused across queries; never retained. */
export interface Contact {
  hit: boolean
  /** Unit normal, from the surface toward the ball centre. */
  nx: number
  ny: number
  /** Penetration depth, positive when overlapping. */
  depth: number
  /** Contact point on the surface, playfield-local. */
  px: number
  py: number
  /** Velocity of the surface at the contact point. */
  svx: number
  svy: number
  material: Material | null
  device: number
}

export function createContact(): Contact {
  return {
    hit: false,
    nx: 0,
    ny: 0,
    depth: 0,
    px: 0,
    py: 0,
    svx: 0,
    svy: 0,
    material: null,
    device: -1,
  }
}

// ---------------------------------------------------------------------------
// Builders — these do the trigonometry once, at load, so the hot path never does
// ---------------------------------------------------------------------------

export function makeSegment(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  material: MaterialName,
  options: {
    oneSided?: boolean
    normal?: readonly [number, number]
    device?: number
    layer?: number
    scatter?: number
    padding?:number
  } = {},
): SegmentCollider {
  const dx = bx - ax
  const dy = by - ay
  const len = Math.sqrt(dx * dx + dy * dy)
  if (len === 0) throw new Error('makeSegment: degenerate segment (a equals b)')

  // The left-hand perpendicular, computed from the face itself.
  const px = -dy / len
  const py = dx / len

  let nx: number
  let ny: number
  if (options.normal) {
    const [sx, sy] = options.normal
    const nl = Math.sqrt(sx * sx + sy * sy)
    if (nl === 0) throw new Error('makeSegment: zero-length normal')
    // A normal that is not perpendicular to its own face is a silent, very confusing bug.
    const dot = (dx * sx + dy * sy) / (len * nl)
    if (Math.abs(dot) > 1e-6) {
      throw new Error(
        `makeSegment: normal is not perpendicular to the face (dot = ${dot.toExponential(2)})`,
      )
    }
    // A supplied normal chooses **which side is solid, and nothing else**. Its value comes
    // from the face, so that describing one surface the two permitted ways produces one
    // surface. Normalising the author's vector instead — `sx / |s|` — takes a different
    // arithmetic path from `-dy / len` and lands one ulp away, which means a machine's
    // geometry would depend on how it was written rather than on what it says. A Builder
    // round trip that loads a definition and re-emits it with the computed normal would
    // then shift the playfield by a bit each time, quietly invalidating a leaderboard whose
    // key is bound to machine content.
    const sameSide = px * sx + py * sy >= 0
    nx = sameSide ? px : -px
    ny = sameSide ? py : -py
  } else {
    nx = px
    ny = py
  }

  return {
    kind: KIND_SEGMENT,
    ax,
    ay,
    bx,
    by,
    nx,
    ny,
    oneSided: options.oneSided ?? false,
    material,
    device: options.device ?? -1,
    layer: options.layer ?? 0,
    scatter: options.scatter ?? -1,
    padding: options.padding ?? 0,
  }
}

export function makeArc(
  cx: number,
  cy: number,
  radius: number,
  angleFrom: number,
  angleTo: number,
  material: MaterialName,
  options: { insideOut?: boolean; device?: number; layer?: number; scatter?: number; padding?:number } = {},
): ArcCollider {
  let span = angleTo - angleFrom
  while (span <= 0) span += TWO_PI
  while (span > TWO_PI) span -= TWO_PI

  // The core's own trigonometry, not the platform's.
  //
  // These three values are not cosmetic and this is not a load-time convenience: `midX`,
  // `midY` and `cosHalfSpan` are compared directly in the hot-path containment test, so a
  // one-ulp disagreement between V8 and IL2CPP gives the two cores *different geometry* on
  // the same machine definition. Every ball that grazes the edge of an arc then diverges,
  // and a replay validated server-side stops matching the client that recorded it.
  const mid = angleFrom + span / 2
  return {
    kind: KIND_ARC,
    cx,
    cy,
    radius,
    insideOut: options.insideOut ?? false,
    midX: cos(mid),
    midY: sin(mid),
    cosHalfSpan: cos(span / 2),
    material,
    device: options.device ?? -1,
    layer: options.layer ?? 0,
    scatter: options.scatter ?? -1,
    padding: options.padding ?? 0,
  }
}

export function makeCircle(
  cx: number,
  cy: number,
  radius: number,
  material: MaterialName,
  options: { device?: number; layer?: number; scatter?: number; padding?:number } = {},
): CircleCollider {
  return {
    kind: KIND_CIRCLE,
    cx,
    cy,
    radius,
    material,
    device: options.device ?? -1,
    layer: options.layer ?? 0,
    scatter: options.scatter ?? -1,
    padding: options.padding ?? 0,
  }
}

/**
 * A polyline, expanded to independent segments at build time.
 *
 * There is deliberately no `KIND_POLYLINE` in the hot path. A compound primitive would have
 * to loop over its own edges inside the innermost query, and the broadphase would have to
 * bucket it by a bounding box that is mostly empty. Expanding once at load costs nothing at
 * runtime and lets each edge carry its own material — which is the whole point of the
 * primitive, since a slingshot body is rubber on its kicking face and plastic elsewhere.
 *
 * Adjacent edges share a vertex, and each segment's endpoint cap is a point, so a ball
 * rolls around a convex corner smoothly. At a concave corner two segments both push, which
 * the contact solver's iteration count already handles.
 *
 * With `closed` and `oneSided`, normals are derived from the winding direction so every
 * face points *out* of the body. Authoring a closed shape with normals pointing inward
 * produces a solid that sucks the ball in, and it is an easy mistake to make by hand.
 */
export function makePolyline(
  points: readonly (readonly [number, number])[],
  material: MaterialName | readonly MaterialName[],
  options: { closed?: boolean; oneSided?: boolean; device?: number; layer?: number; scatter?: number; padding?:number } = {},
): SegmentCollider[] {
  const closed = options.closed ?? false
  const edgeCount = closed ? points.length : points.length - 1
  if (points.length < 2 || edgeCount < 1) {
    throw new Error(`makePolyline: needs at least 2 points, got ${points.length}`)
  }
  if (Array.isArray(material) && material.length !== edgeCount) {
    throw new Error(
      `makePolyline: ${edgeCount} edges but ${material.length} materials — supply one per edge, or a single name for all`,
    )
  }

  // Shoelace signed area. Positive is counter-clockwise, whose outward normal is the right
  // perpendicular; negative is clockwise, whose outward normal is the left perpendicular.
  let twiceArea = 0
  if (closed) {
    for (let i = 0; i < points.length; i++) {
      const p = points[i]!
      const q = points[(i + 1) % points.length]!
      twiceArea += p[0] * q[1] - q[0] * p[1]
    }
    if (twiceArea === 0) {
      throw new Error('makePolyline: closed polyline encloses no area, so it has no outside')
    }
  }
  const rightHanded = twiceArea > 0

  const out: SegmentCollider[] = []
  for (let i = 0; i < edgeCount; i++) {
    const a = points[i]!
    const b = points[(i + 1) % points.length]!
    const edgeMaterial = Array.isArray(material) ? material[i]! : (material as MaterialName)

    const segmentOptions: {
      oneSided?: boolean
      normal?: readonly [number, number]
      device?: number
      layer?: number
      scatter?: number
      padding?:number
    } = { device: options.device ?? -1, layer: options.layer ?? 0, scatter: options.scatter ?? -1,padding:options.padding??0 }

    if (options.oneSided) {
      segmentOptions.oneSided = true
      if (closed) {
        const dx = b[0] - a[0]
        const dy = b[1] - a[1]
        segmentOptions.normal = rightHanded ? [dy, -dx] : [-dy, dx]
      }
    }

    out.push(makeSegment(a[0], a[1], b[0], b[1], edgeMaterial, segmentOptions))
  }
  return out
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * Segment contact.
 *
 * `bx, by` is the ball centre in *collider space* — the caller has already subtracted any
 * machine-frame nudge displacement, so nudging costs nothing here.
 */
export function segmentContact(
  s: SegmentCollider,
  bx: number,
  by: number,
  r: number,
  out: Contact,
): boolean {
  const ex = s.bx - s.ax
  const ey = s.by - s.ay
  const len2 = ex * ex + ey * ey

  // Closest point on the segment, clamped to the endpoints so the caps are round.
  let t = ((bx - s.ax) * ex + (by - s.ay) * ey) / len2
  if (t < 0) t = 0
  else if (t > 1) t = 1

  const px = s.ax + ex * t
  const py = s.ay + ey * t

  let dx = bx - px
  let dy = by - py
  let dist = Math.sqrt(dx * dx + dy * dy)

  if (dist >= r) return false

  if (s.oneSided) {
    // Ignore contact from behind the face entirely — this is what makes a slingshot fire
    // only when struck on its active side, and lets a ball pass through a one-way gate.
    if (dx * s.nx + dy * s.ny < 0) return false
  }

  if (dist < 1e-12) {
    // Ball centre exactly on the surface: fall back to the face normal rather than
    // dividing by zero and producing NaN that poisons the whole simulation.
    dx = s.nx
    dy = s.ny
    dist = 0
  } else {
    const inv = 1 / dist
    dx *= inv
    dy *= inv
  }

  out.hit = true
  out.nx = dx
  out.ny = dy
  out.depth = r - dist
  out.px = px
  out.py = py
  out.device = s.device
  return true
}

/** Arc contact, containing (`insideOut = false`) or obstructing (`true`). */
export function arcContact(
  a: ArcCollider,
  bx: number,
  by: number,
  r: number,
  out: Contact,
): boolean {
  const dx = bx - a.cx
  const dy = by - a.cy
  const dist = Math.sqrt(dx * dx + dy * dy)
  if (dist < 1e-12) return false // ball at the arc centre: no meaningful normal

  const inv = 1 / dist
  const ux = dx * inv
  const uy = dy * inv

  // Angular containment by dot product against the bisector — no atan2 in the hot path.
  if (ux * a.midX + uy * a.midY < a.cosHalfSpan) return false

  if (a.insideOut) {
    const depth = a.radius + r - dist
    if (depth <= PENETRATION_SLOP) return false
    out.nx = ux
    out.ny = uy
    out.depth = depth
    out.px = a.cx + ux * a.radius
    out.py = a.cy + uy * a.radius
  } else {
    const depth = dist - (a.radius - r)
    if (depth <= PENETRATION_SLOP) return false
    out.nx = -ux
    out.ny = -uy
    out.depth = depth
    out.px = a.cx + ux * a.radius
    out.py = a.cy + uy * a.radius
  }

  out.hit = true
  out.device = a.device
  return true
}

/** Circle contact. The ball always stays outside — posts, bumper skirts, captive balls. */
export function circleContact(
  c: CircleCollider,
  bx: number,
  by: number,
  r: number,
  out: Contact,
): boolean {
  const dx = bx - c.cx
  const dy = by - c.cy
  const sum = c.radius + r
  const d2 = dx * dx + dy * dy
  if (d2 >= sum * sum) return false

  const dist = Math.sqrt(d2)
  if (dist < 1e-12) return false

  const inv = 1 / dist
  out.hit = true
  out.nx = dx * inv
  out.ny = dy * inv
  out.depth = sum - dist
  out.px = c.cx + out.nx * c.radius
  out.py = c.cy + out.ny * c.radius
  out.device = c.device
  return true
}

/**
 * Tapered-capsule contact, used by the flipper bat and the plunger tip.
 *
 * The radius is interpolated along the segment rather than solving the exact rounded-cone
 * surface. With the real bat taper — 11 mm at the pivot to 7.5 mm at the tip over 76.2 mm,
 * a slope of 0.046 — the approximation error is under 0.2 mm, well inside the contact
 * solver's own slop. Solving the true cone would cost a branch-heavy gradient for no
 * behaviour a player could detect.
 */
export function taperedCapsuleContact(
  ax: number,
  ay: number,
  bx2: number,
  by2: number,
  ra: number,
  rb: number,
  bx: number,
  by: number,
  r: number,
  out: Contact,
): boolean {
  const ex = bx2 - ax
  const ey = by2 - ay
  const len2 = ex * ex + ey * ey
  if (len2 < 1e-18) return false

  let t = ((bx - ax) * ex + (by - ay) * ey) / len2
  if (t < 0) t = 0
  else if (t > 1) t = 1

  const px = ax + ex * t
  const py = ay + ey * t
  const surfaceRadius = ra + (rb - ra) * t

  const dx = bx - px
  const dy = by - py
  const sum = surfaceRadius + r
  const d2 = dx * dx + dy * dy
  if (d2 >= sum * sum) return false

  const dist = Math.sqrt(d2)
  if (dist < 1e-12) return false

  const inv = 1 / dist
  out.hit = true
  out.nx = dx * inv
  out.ny = dy * inv
  out.depth = sum - dist
  out.px = px + out.nx * surfaceRadius
  out.py = py + out.ny * surfaceRadius
  return true
}

/** Dispatch to the right query for a static collider. */
export function colliderContact(
  c: Collider,
  bx: number,
  by: number,
  r: number,
  out: Contact,
): boolean {
  r+=c.padding??0
  switch (c.kind) {
    case KIND_SEGMENT:
      return segmentContact(c, bx, by, r, out)
    case KIND_ARC:
      return arcContact(c, bx, by, r, out)
    case KIND_CIRCLE:
      return circleContact(c, bx, by, r, out)
    default: {
      // Exhaustiveness: adding a primitive without handling it here is a compile error.
      const never: never = c
      throw new Error(`colliderContact: unhandled collider ${String(never)}`)
    }
  }
}

/** Axis-aligned bounds of a static collider, for broadphase bucketing. */
export function colliderBounds(
  c: Collider,
): { minX: number; minY: number; maxX: number; maxY: number } {
  const padding=c.padding??0
  switch (c.kind) {
    case KIND_SEGMENT:
      return {
        minX: Math.min(c.ax, c.bx)-padding,
        minY: Math.min(c.ay, c.by)-padding,
        maxX: Math.max(c.ax, c.bx)+padding,
        maxY: Math.max(c.ay, c.by)+padding,
      }
    case KIND_ARC:
    case KIND_CIRCLE:
      // Conservative: the full circle. Arcs are short enough that tightening this to the
      // true swept bounds would buy nothing measurable.
      return {
        minX: c.cx - c.radius-padding,
        minY: c.cy - c.radius-padding,
        maxX: c.cx + c.radius+padding,
        maxY: c.cy + c.radius+padding,
      }
    default: {
      const never: never = c
      throw new Error(`colliderBounds: unhandled collider ${String(never)}`)
    }
  }
}
